/**
 * The editor's clipboard (1.7.3): what ⌘C or ⌘X copied (ops.copyItems), kept in a module store and in localStorage
 * under `prism.clipboard`, so a copy works between sequences and tabs of one browser. Every storage access is in a
 * try/catch (private mode, blocked or full storage: the module store still works); what is read back goes through
 * parseClipboard, so a payload of another version or a damaged one is ignored.
 */
import { parseClipboard, type ClipboardPayload } from '@prism/contracts';
import { createStore } from './playheadClock';

export const CLIPBOARD_KEY = 'prism.clipboard';

const store = createStore<ClipboardPayload | null>(null);

/** Keep a copy (null clears it). */
export function writeClipboard(payload: ClipboardPayload | null): void {
  store.set(payload);
  try {
    if (payload) localStorage.setItem(CLIPBOARD_KEY, JSON.stringify(payload));
    else localStorage.removeItem(CLIPBOARD_KEY);
  } catch {
    /* storage unavailable: the copy stays in this tab */
  }
}

/** The latest copy: localStorage first (another tab may have copied since), then this tab's. */
export function readClipboard(): ClipboardPayload | null {
  try {
    const raw = localStorage.getItem(CLIPBOARD_KEY);
    if (raw) {
      const parsed = parseClipboard(raw);
      if (parsed) return parsed;
    }
  } catch {
    /* storage unavailable */
  }
  return store.get();
}

/** Whether there is anything to paste. */
export const hasClipboard = (): boolean => readClipboard() !== null;

/** Follow this tab's copies (for enabling Paste). */
export const subscribeClipboard = (fn: (p: ClipboardPayload | null) => void): (() => void) => store.subscribe(fn);
